import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { throttle } from 'lodash-es';
import Helmet from 'react-helmet';
import { IntlProvider } from 'react-intl-next';
import { useDispatch, useSelector } from 'react-redux';
import { useRouterActions, useResource } from 'react-resource-router';

import { Grid, GridColumn } from '@atlaskit/page';
import { Box } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import {
  ContextualAnalyticsData,
  FireScreenAnalytics,
  SCREEN,
} from '@atlassian/analytics-bridge';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import Sidebar from 'src/app/sidebar';
import { FocusedPageHeader } from 'src/components/accessibility';
import { PipelineMessages } from 'src/components/pipelines/common';
import Filters from 'src/components/pipelines/components/filters';
import PipelinesList from 'src/components/pipelines/components/list';
import { PipelineListSidebar } from 'src/components/pipelines/components/list-sidebar';
import {
  ALLOWANCE_DISMISSED_KEY,
  PipelinesWebsocketHookEvent,
  SELECTED_BRANCH_REGEX,
  PIPELINE_PAGE_SIZE,
} from 'src/components/pipelines/constants';
import { usePipelineDefinitions } from 'src/components/pipelines/hooks/use-pipeline-definitions';
import { useWebsocket } from 'src/components/pipelines/hooks/use-websocket';
import { Branch } from 'src/components/pipelines/models';
import { loadPipelineBranchResource } from 'src/components/pipelines/resources/pipeline-branch';
import { loadPipelinesResource } from 'src/components/pipelines/resources/pipelines';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useLocation, useParams } from 'src/router/hooks';
import Breadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import { getUpToSmallBreakpointState } from 'src/selectors/global-selectors';
import { publishScreenEvent } from 'src/utils/analytics/publish';
import store from 'src/utils/store';

import { removePipelinesData } from '../../redux/actions/pipelines';
import {
  getAccount,
  getCapabilities,
  getIsLoadingPipelines,
  getPipelineCreators,
  getPipelines,
  getPipelinesSize,
  getRepository,
} from '../../redux/selectors/pipelines';
import { prepareBranchList } from '../../utils/getPipelineBranchList';
import {
  buildFilterPathParam,
  parseFilterPathParam,
  getFilterQuery,
  getFilterPathname,
} from '../../utils/pipeline-filter-query';
import queryBranches from '../../utils/query-branches';

import PipelinesListActions from './pipelines-list-actions/pipelines-list-actions';
import { Container } from './styles';
import { getPageNumber, shouldRefreshPipelines } from './utils';

const WEBSOCKET_REFRESH_THROTTLE = 5000;
const WEBSOCKET_CREATED_DELAY = 500;

const PipelinesPage = () => {
  const dispatch = useDispatch();
  const { push } = useRouterActions();
  const location = useLocation();

  const params = useParams<{
    repositoryFullSlug: string;
    branch: string;
    page: string;
  }>();
  const { repositoryFullSlug, branch: filterBranch, page: filterPage } = params;
  const filterPageNumber = getPageNumber(filterPage);
  const isSidebarEnabled = useStatsigGate(
    StatsigFeatureKeys.isPipelineListSidebarEnabled
  );

  const account = useSelector(getAccount);
  const capabilities = useSelector(getCapabilities);
  const repository = useSelector(getRepository);
  const pipelines = useSelector(getPipelines);
  const pipelinesSize = useSelector(getPipelinesSize);
  const pipelineCreators = useSelector(getPipelineCreators);
  const isLoadingPipelines = useSelector(getIsLoadingPipelines);
  const isUpToSmallBreakpointActive = useSelector(getUpToSmallBreakpointState);
  const [selectedBranchCommitHash, setSelectedBranchCommitHash] =
    useState<string>('');
  const [createdPipelineUuids, setCreatedPipelinesUuids] = useState<string[]>(
    []
  );

  const pipelinesFrom = (filterPageNumber - 1) * PIPELINE_PAGE_SIZE;
  const pipelinesTo = pipelinesFrom + PIPELINE_PAGE_SIZE;
  const currentPipelines = pipelines
    .slice(pipelinesFrom, pipelinesTo)
    .filter(pipeline => !!pipeline);

  const allowanceDismissedKey = useMemo(
    () => `${ALLOWANCE_DISMISSED_KEY}-${repository.uuid}`,
    [repository.uuid]
  );

  const { data: branchData } = useResource(loadPipelineBranchResource);

  const selectedBranch = useMemo(() => {
    const branchMatches = location.pathname.match(SELECTED_BRANCH_REGEX);
    if (branchMatches && branchMatches[1]) {
      const branchName = decodeURIComponent(branchMatches[1]);
      const targetHash = selectedBranchCommitHash || branchData?.target?.hash;
      return new Branch({
        name: branchName,
        target: { hash: targetHash },
      });
    }
    return undefined;
  }, [location.pathname, selectedBranchCommitHash, branchData]);

  const { loading: pipelineDefinitionsLoading, data: pipelineDefinitions } =
    usePipelineDefinitions(
      repository,
      selectedBranch?.target?.hash || filterBranch
    );

  const { refresh: refreshPipelinesResource } = useResource(
    loadPipelinesResource
  );

  const throttledRefreshPipelinesResource = useMemo(
    () => throttle(refreshPipelinesResource, WEBSOCKET_REFRESH_THROTTLE),
    [refreshPipelinesResource]
  );

  const onPipelineUpdated = useCallback(
    ({ pipelineUuid }: any) => {
      if (
        shouldRefreshPipelines(
          pipelineUuid,
          currentPipelines,
          createdPipelineUuids
        )
      ) {
        throttledRefreshPipelinesResource();
        setCreatedPipelinesUuids(
          createdPipelineUuids.filter(e => e !== pipelineUuid)
        );
      }
    },
    [currentPipelines, throttledRefreshPipelinesResource, createdPipelineUuids]
  );

  useWebsocket({
    event: PipelinesWebsocketHookEvent.PipelineUpdated,
    onMessage: onPipelineUpdated,
    criteria: { repositoryUuid: repository.uuid },
  });

  const onPipelineCreated = useCallback(
    ({ pipelineUuid }: any) => {
      if (filterPageNumber === 1) {
        /*
          Immediately fetching pipelines on pipeline_created can cause incorrect
          results because ElasticSearch wont always have indexed them in time.
          To avoid this race condition we maintain a list of created pipeline uuids
          and fetch on the corresponding pipeline_updated event with a minor delay
          to avoid the first update that happens immediately after creation.
        */
        setTimeout(() => {
          setCreatedPipelinesUuids([...createdPipelineUuids, pipelineUuid]);
        }, WEBSOCKET_CREATED_DELAY);
      }
    },
    [filterPageNumber, createdPipelineUuids]
  );

  useWebsocket({
    event: PipelinesWebsocketHookEvent.PipelineCreated,
    onMessage: onPipelineCreated,
    criteria: { repositoryUuid: repository.uuid },
  });

  useEffect(() => {
    publishScreenEvent('pipelinesListScreen', {
      repositoryUuid: repository.uuid,
    });
    return () => {
      dispatch(removePipelinesData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryFilterBranches = useCallback(
    (search: string) =>
      queryBranches(repository, search).then((data: any) => {
        const branches: any[] = data?.values || [];
        const existingMainBranch = branches.find(
          b => b.name === repository.mainbranch
        );

        const mainBranch = new Branch({
          name: repository.mainbranch,
          target: existingMainBranch?.target,
        });

        const preparedBranches = prepareBranchList(
          branches,
          search,
          mainBranch.name
        );
        return [mainBranch].concat(preparedBranches);
      }),
    [repository]
  );

  const generateFilterUrl = useCallback(
    (filters: any, shouldPush = true) => {
      const filterPath = buildFilterPathParam(filters);
      const page = shouldPush ? '1' : filterPageNumber;
      let url = `/${repositoryFullSlug}/pipelines/results/`;

      if (filterBranch) {
        url += `branch/${encodeURIComponent(filterBranch)}/`;
      }
      url += `page/${page}${filterPath}`;
      return `${url}${location.search}`;
    },

    [filterPageNumber, repositoryFullSlug, filterBranch, location.search]
  );

  const onUpdateFilter = useCallback(
    (filters: any, shouldPush = true) => {
      const url = generateFilterUrl(filters, shouldPush);

      if (shouldPush) {
        dispatch(removePipelinesData());
        push(url);
      } else {
        // Updates url without reloading
        window.history.replaceState(null, '', url);
      }
    },
    [push, generateFilterUrl, dispatch]
  );

  const onBranchChange = useCallback(
    (selectedOption: any) => {
      dispatch(removePipelinesData());
      const filterPath = buildFilterPathParam({
        ...parseFilterPathParam(location.pathname),
        selectedPipelineType: null,
      });
      if (selectedOption) {
        push(
          `/${repositoryFullSlug}/pipelines/results/branch/${encodeURIComponent(
            selectedOption.value
          )}/page/1${filterPath}${location.search}`
        );
        setSelectedBranchCommitHash(selectedOption.target.hash);
      } else {
        push(
          `/${repositoryFullSlug}/pipelines/results/page/${filterPageNumber}${filterPath}${location.search}`
        );
      }
    },
    [dispatch, push, filterPageNumber, location, repositoryFullSlug]
  );

  const onPageChange = useCallback(
    /* eslint-disable @typescript-eslint/no-unused-vars */
    // @ts-ignore
    (event: any, page: number) => {
      const onSamePage = page === filterPageNumber;
      if (onSamePage) {
        return;
      }

      push(
        `/${repositoryFullSlug}/pipelines${getFilterPathname(
          page,
          location.pathname,
          filterBranch
        )}${location.search}`
      );
    },
    [push, location, filterPageNumber, filterBranch, repositoryFullSlug]
  );

  return (
    <ContextualAnalyticsData sourceName="pipelines" sourceType={SCREEN}>
      <FireScreenAnalytics attributes={{ repositoryUuid: repository.uuid }} />
      <Helmet defer={false} title="Pipelines" />
      <Box xcss={Container}>
        <Grid
          layout="fluid"
          spacing={isUpToSmallBreakpointActive ? 'cosy' : 'comfortable'}
        >
          <GridColumn>
            <FocusedPageHeader
              breadcrumbs={<Breadcrumbs />}
              actions={<PipelinesListActions selectedBranch={selectedBranch} />}
            >
              Pipelines
            </FocusedPageHeader>
            <IntlProvider locale="en">
              {capabilities.hasFetchedCapabilities && repository.hasFetched && (
                <PipelineMessages
                  repository={repository}
                  capabilities={capabilities}
                  account={account}
                  isAllowanceMessageDismissed={
                    !!store.get(allowanceDismissedKey)
                  }
                  onDismissMessage={() =>
                    store.setStrictlyNecessaryItem(allowanceDismissedKey, true)
                  }
                />
              )}
              <Filters
                url={location.pathname}
                repositoryFullSlug={repositoryFullSlug}
                onUpdateFilter={onUpdateFilter}
                pipelineDefinitions={pipelineDefinitions}
                pipelineDefinitionsLoading={pipelineDefinitionsLoading}
                getBranches={queryFilterBranches}
                branchValue={selectedBranch}
                onBranchChange={onBranchChange}
                getFilterQuery={getFilterQuery}
                users={pipelineCreators}
                showUsersFilter
              />
              <UFOLoadHold
                name="pipelines-list-loading"
                hold={isLoadingPipelines}
              >
                <PipelinesList
                  isLoading={isLoadingPipelines}
                  onPageChange={onPageChange}
                  page={filterPageNumber}
                  currentPipelines={currentPipelines}
                  pipelinesSize={pipelinesSize}
                  repository={repository}
                  capabilities={capabilities}
                />
              </UFOLoadHold>
            </IntlProvider>
          </GridColumn>
          {isSidebarEnabled && (
            <Sidebar>
              <PipelineListSidebar />
            </Sidebar>
          )}
        </Grid>
      </Box>
    </ContextualAnalyticsData>
  );
};

export default React.memo(PipelinesPage);
